import React, { useEffect, useState } from 'react';
import DenotedGardenMenuItem from './DenotedGardenMenuItem';
import DenotedGardenMenuBottomSection from './DenotedGardenMenuBottomSection';
import './GardenStyling.css';
import '../css/gridGardenDesignerStylings/DenotedGardenSectionMenuStylings.css';
import {useNavigate} from "react-router-dom";

const DenotedGardenSectionMenu = ({ denoteActive, denotedPlots, inputWidth, inputHeight, onDeleteGarden, onSave, onRotate, onDuplicate, onNextSteps, savedGardenPlots }) => {
    const [sortedPlotsMap, setSortedPlotsMap] = useState({});
    const [gardenMatrices, setGardenMatrices] = useState({});
    // const [savedGardenPlots, setSavedGardenPlots] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [gardenToDelete, setGardenToDelete] = useState(null);
    const navigate = useNavigate();

    // Function to parse and sort plots for a specific garden group
    const sortPlotsForGroup = (groupKey) => {
        const cellNumbers = groupKey.split('-')
            .map(num => parseInt(num, 10))
            .filter(num => !isNaN(num));
        return cellNumbers.sort((a, b) => a - b);
    };

    // Function to create a hashmap of garden names to sorted plots
    const createSortedPlotsMap = (denotedPlots) => {
        const plotsMap = {};

        // Iterate through each group in denotedPlots
        Object.entries(denotedPlots).forEach(([groupKey, gardenName]) => {
            plotsMap[gardenName] = sortPlotsForGroup(groupKey);
        });

        return plotsMap;
    };

    // Function to convert plot numbers to a 2D matrix of 1's and 0's
    function convertPlotsToMatrix(plotNumbers, width = 25, height = 25) {
        if (!plotNumbers || plotNumbers.length === 0) {
            throw new Error("Plot numbers cannot be empty");
        }

        // Initialize a 2D matrix with dimensions height x width filled with zeros
        const matrix = Array.from({ length: height }, () => Array(width).fill(0));

        plotNumbers.forEach(plotNum => {
            // Calculate row and column from the plot number
            const row = Math.floor(plotNum / width);
            const col = plotNum % width;

            // Ensure the row and column are within bounds
            if (row >= 0 && row < height && col >= 0 && col < width) {
                matrix[row][col] = 1; // Mark the cell as 1
            } else {
                console.warn(`Plot number ${plotNum} is out of bounds`);
            }
        });

        return matrix;
    }


    // Functions to handle delete button click for each menu item
    const handleDeleteClick = (gardenName) => {
        setGardenToDelete(gardenName);
        setShowModal(true);
    };

    const confirmDelete = () => {
        if (gardenToDelete) {
            // Update local matrices state
            setGardenMatrices(prevMatrices => {
                const newMatrices = { ...prevMatrices };
                delete newMatrices[gardenToDelete];
                return newMatrices;
            });

            // Call parent component's deletion function with garden name
            if (typeof onDeleteGarden === 'function') {
                onDeleteGarden(gardenToDelete);
            }

            setGardenToDelete(null);
        }
        setShowModal(false);
    };

    const cancelDelete = () => {
        setGardenToDelete(null);
        setShowModal(false);
    };

    const handleSaveClick = (gardenName, matrix, soilType, hardiness, sunlight, maintenance) => {
        //pass the saved data back to the in ground garden designer page
        onSave(gardenName, matrix, soilType, hardiness, sunlight, maintenance);
    }

    const rotateMatrixClockwise = (matrix) => {
        // Convert the string representation to a 2D array
        const rows = matrix.split('\n');
        const grid = rows.map(row => row.split(',').map(Number));

        // Get dimensions
        const n = grid.length;
        const m = grid[0].length;

        // Create a new matrix with swapped dimensions
        const rotated = Array.from({ length: m }, () => Array(n).fill(0));

        // Fill the new matrix (90° clockwise rotation)
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < m; j++) {
                rotated[j][n - 1 - i] = grid[i][j];
            }
        }

        // Convert back to string format
        return rotated.map(row => row.join(',')).join('\n');
    };

// Then modify handleRotateClick
    const handleRotateClick = (gardenName) => {
        // Find the garden matrix
        const matrix = gardenMatrices[gardenName];
        if (!matrix) return;

        // Rotate the matrix
        const rotatedMatrix = rotateMatrixClockwise(matrix.join(',\n'));

        // Update local state
        setGardenMatrices(prev => ({
            ...prev,
            [gardenName]: rotatedMatrix.split('\n').map(row => row.split(',').map(Number))
        }));

        // Pass up to parent component
        onRotate(gardenName, rotatedMatrix);
    };

    const handleDuplicateClick = (plotkey, gardenName) => {
        onDuplicate(plotkey, gardenName);
    }

    const handleNextStepsClick = () => {
        onNextSteps();
    }

    // Function to handle props change
    const handlePropsChange = () => {
        // Setup temporary matrices object
        const tempMatrices = {};

        // Process each garden plot
        Object.entries(denotedPlots).forEach(([gardenName, plotArray]) => {
            const matrix = convertPlotsToMatrix(plotArray, inputWidth, inputHeight);
            tempMatrices[gardenName] = matrix;
        });

        // Update state once with all matrices
        setGardenMatrices(tempMatrices);
        setSortedPlotsMap(denotedPlots);
    };

    const handleRestart = () => {
        navigate("/");
    }

    // Call the function whenever props change
    useEffect(() => {
        handlePropsChange();
    }, [denoteActive, denotedPlots]);

    return (
        <div className={`denoted-garden-form-container ${denoteActive ? 'active-menu' : ''}`}>
            <h2 className={"denoted-garden-form-container-title"}>Denoted Plots</h2>
            <div className={`denoted-garden-menu-items-container`}>
                {Object.keys(gardenMatrices).length > 0 ? (
                    Object.entries(gardenMatrices).map(([gardenName, sortedPlots]) => (
                        <DenotedGardenMenuItem
                            key={gardenName}
                            gardenName={gardenName}
                            denotedPlots={denotedPlots}
                            gardenCells={sortedPlots.join(',\n')}
                            width={inputWidth}
                            height={inputHeight}
                            onDelete={handleDeleteClick}
                            onSave={handleSaveClick}
                            onRotate={handleRotateClick}
                            onDuplicate={handleDuplicateClick}
                        />
                    ))
                ) : (
                    <div className="no-denotions">
                        Select the denote tool and then a garden to start denoting!
                    </div>
                )}
            </div>

            <div className={"denoted-garden-menu-bottom-div"}>
                <DenotedGardenMenuBottomSection gardenInformation={gardenMatrices} savedGardenPlots={savedGardenPlots} onNext={handleNextStepsClick} onRestart={handleRestart}/>
            </div>

            {showModal && (
                <div className="delete-modal-overlay">
                    <div className="modal-content">
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete <strong>{gardenToDelete}</strong>?</p>
                        <p>
                            <strong>
                                <span className={`warning-text`}>You cannot undo this action! </span>
                            </strong>
                        </p>
                        <div className="modal-buttons">
                            <button className="confirm-button" onClick={confirmDelete}>Yes, Delete</button>
                            <button className="cancel-button" onClick={cancelDelete}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DenotedGardenSectionMenu;